import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { color } from '@/colors';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { Flex, FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useUploadMediaAsset, type FileType } from '@/hooks/useUploadMediaAsset';
import { validateFile } from '@/utils/nodeInputValidation';
import type { FileKind } from 'web';
import type { MediaAsset } from '@/types/api/assets';
import type { MediaIteratorInput } from '@/types/design-app.types';

const THUMBNAIL_SIZE = 68;
const MAX_FILES = 50;

interface DesignAppMediaIteratorProps {
  id: string;
  value: MediaIteratorInput['value'];
  onChange: (id: string, value: MediaIteratorInput['value']) => void;
  acceptedFileType: FileType;
  disabled: boolean;
  isLoading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  error: boolean;
  helperText: string | null;
}

export default function DesignAppMediaIterator({
  id,
  value,
  onChange,
  acceptedFileType,
  disabled,
  isLoading,
  setIsUploading,
  error,
  helperText,
}: DesignAppMediaIteratorProps) {
  const files = value?.files ?? [];

  // Ref keeps the latest files so upload callbacks that fire after a stale
  // closure was captured (e.g. multi-file drop) still read the current list.
  const filesRef = useRef(files);
  filesRef.current = files;

  const handleUpload = useCallback(
    (asset: MediaAsset) => {
      const fileKind = validateFile(asset);
      if (!fileKind) return;
      onChange(id, { files: [...filesRef.current, fileKind] });
    },
    [id, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(id, { files: filesRef.current.filter((_, i) => i !== index) });
    },
    [id, onChange],
  );

  const { dropzoneState, isInProgress } = useUploadMediaAsset({
    disabled: disabled || isLoading,
    onUpload: handleUpload,
    allowMultiple: true,
    setIsUploading,
    acceptedFileType,
  });

  const { getRootProps, getInputProps, open } = dropzoneState;

  if (isLoading) return null;

  const canAdd = !disabled && files.length < MAX_FILES;

  return (
    <FlexCol
      sx={{
        width: '100%',
        gap: 1,
        maxHeight: 240,
        overflow: 'hidden auto',
        scrollbarColor: `${color.White16_T} transparent`,
      }}
    >
      <Flex
        {...getRootProps()}
        sx={{
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input {...getInputProps()} />

        {canAdd && (
          <FlexCenHorVer
            onClick={open}
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              borderRadius: '4px',
              border: `1px solid ${color.White16_T}`,
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { borderColor: color.White40_T },
            }}
          >
            <PlusIcon style={{ width: 16, height: 16, color: color.White40_T }} />
          </FlexCenHorVer>
        )}

        {files.map((file, index) => (
          <FileThumbnail
            key={`${file.url}-${index}`}
            file={file}
            index={index}
            onRemove={handleRemove}
            disabled={disabled}
          />
        ))}

        {isInProgress && (
          <FlexCenHorVer
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              borderRadius: '4px',
              border: `1px solid ${color.White16_T}`,
              flexShrink: 0,
            }}
          >
            <TextSmRg color={color.White40_T}>...</TextSmRg>
          </FlexCenHorVer>
        )}
      </Flex>

      <FlexCenVer sx={{ justifyContent: 'flex-end' }}>
        <TextSmRg color={color.White40_T}>
          {files.length}/{MAX_FILES}
        </TextSmRg>
      </FlexCenVer>

      {error && helperText && (
        <TextSmRg color={color.Weavy_Error} sx={{ mt: 0.5 }}>
          {helperText}
        </TextSmRg>
      )}
    </FlexCol>
  );
}

function FileThumbnail({
  file,
  index,
  onRemove,
  disabled,
}: {
  file: FileKind;
  index: number;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  const { isHovered, ...hoverProps } = useIsHovered();
  const thumbnailUrl = 'thumbnailUrl' in file ? (file.thumbnailUrl as string) : undefined;

  return (
    <Box
      {...hoverProps}
      sx={{
        position: 'relative',
        width: THUMBNAIL_SIZE,
        height: THUMBNAIL_SIZE,
        borderRadius: '4px',
        border: `1px solid ${color.White16_T}`,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <img
        src={thumbnailUrl || file.url}
        alt={`file-${index}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '4px' }}
      />

      {!disabled && isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
          }}
        >
          <AppXBtn size={16} onClick={() => onRemove(index)} />
        </Box>
      )}
    </Box>
  );
}
